import { type FC, useState } from "react";
import {
  Toolbar,
  Checkbox,
  Button,
  Typography,
  Divider,
  Menu,
} from "@mui/material";
import { useSnackbar } from "notistack";
import {
  ActionMenuItem,
  ToolbarProgress,
  useConfirmAction,
} from "@/shared/components";
import { useTranslation } from "react-i18next";
import {
  useUpdateContentsMutation,
  useDeleteContentsMutation,
} from "@/partner/entities/contents";
import type { StreamsItem } from "@/partner/entities/streams";

interface StreamsToolbarProps {
  indeterminate: boolean;
  numSelected: number;
  nodes: StreamsItem[];
  onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
  selectedItems: { id: string }[];
  onCloseSelected: () => void;
}

// Стримы — те же Content-документы (type=STREAM), что и видео, поэтому
// bulk-мутации переиспользуются как есть из entities/contents (см. как
// StreamForm уже переиспользует contents.form.* — тот же общий домен).
export const StreamsToolbar: FC<StreamsToolbarProps> = ({
  indeterminate,
  numSelected,
  nodes,
  onSelectAllClick,
  selectedItems,
  onCloseSelected,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { confirm, dialog: confirmDialog } = useConfirmAction();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const [updateContents, { loading: updateLoading }] =
    useUpdateContentsMutation({
      onCompleted: () => {
        onCloseSelected();
        enqueueSnackbar(t("streams.toolbar.updateSuccess"), {
          variant: "success",
        });
      },
      onError: (error: { message: string }) => {
        enqueueSnackbar(error.message, { variant: "error" });
      },
      refetchQueries: ["StreamsList"],
    });

  const [deleteContents, { loading: deleteLoading }] =
    useDeleteContentsMutation({
      onCompleted: () => {
        onCloseSelected();
        enqueueSnackbar(t("streams.toolbar.deleteSuccess"), {
          variant: "success",
        });
      },
      onError: (error: { message: string }) => {
        enqueueSnackbar(error.message, { variant: "error" });
      },
      refetchQueries: ["StreamsList"],
    });

  const onPublishSelected = () => {
    updateContents({
      variables: {
        ids: selectedItems.map((item) => item.id),
        input: {
          publishAt: new Date().toISOString(),
        },
      },
    });
  };

  const onUnpublishSelected = () => {
    updateContents({
      variables: {
        ids: selectedItems.map((item) => item.id),
        input: {
          publishAt: null,
        },
      },
    });
  };

  const onDeleteSelected = () => {
    confirm({
      title: t("common.actions.delete"),
      text: t("streams.toolbar.deleteConfirm"),
      // Промис наружу: диалог держится открытым с полосой прогресса,
      // пока идёт запрос, и закрывается сам по успеху.
      onConfirm: () =>
        deleteContents({
          variables: {
            ids: selectedItems.map((item) => item.id),
          },
        }),
    });
  };

  return (
    <Toolbar sx={{ position: "relative" }}>
      <Checkbox
        color="default"
        indeterminate={indeterminate}
        checked={numSelected === nodes.length}
        onChange={onSelectAllClick}
        sx={{
          marginLeft: "-16px",
          marginRight: {
            xs: 0,
            sm: "16px",
          },
        }}
      />

      <Typography>
        {t("common.toolbar.selectedCount", { count: numSelected })}
      </Typography>

      <Divider
        orientation="vertical"
        variant="middle"
        sx={{
          mx: 1.25,
          height: "2em",
        }}
      />

      <Button
        color="inherit"
        onClick={handleClick}
        disabled={numSelected === 0}
      >
        {t("common.actions.actions")}
      </Button>

      <Menu
        id="streams-actions-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
      >
        <ActionMenuItem
          disabled={numSelected === 0}
          loading={updateLoading}
          onClick={() => {
            onPublishSelected();
            handleClose();
          }}
        >
          {t("common.actions.publish")}
        </ActionMenuItem>

        <ActionMenuItem
          disabled={numSelected === 0}
          loading={updateLoading}
          onClick={() => {
            onUnpublishSelected();
            handleClose();
          }}
        >
          {t("common.actions.unpublish")}
        </ActionMenuItem>

        <ActionMenuItem
          disabled={numSelected === 0}
          loading={deleteLoading}
          onClick={() => {
            onDeleteSelected();
            handleClose();
          }}
        >
          {t("common.actions.delete")}
        </ActionMenuItem>
      </Menu>

      <ToolbarProgress loading={updateLoading || deleteLoading} />
      {confirmDialog}
    </Toolbar>
  );
};
